<template>
    <div class="help-page">
        <div v-if="isLoading" class="loading-overlay"><p>Carregando...</p></div>
        <div v-if="msgAlerta" class="info-alert row-space" @click="msgAlerta = null">{{ msgAlerta }}</div>

        <div class="help-header row-space">
            <div>
                <h2>Central de Ajuda</h2>
                <p>Guias rápidos e tutoriais para utilização do SG-SIM.</p>
            </div>

            <button class="btn-small" @click="closeForm">Voltar</button>
        </div>

        <div v-if="!isLoading && gruposAjuda.length === 0" class="empty-state">
            Nenhum tutorial disponível no momento.
        </div>

        <div
            v-for="grupo in gruposAjuda"
            :key="grupo.nome"
            class="help-group row-space"
        >
            <div
                class="help-group-header"
                @click="alternarGrupo(grupo.nome)"
            >
                <div>
                    <h3>{{ grupo.nome }}</h3>
                    <span>{{ grupo.itens.length }} tutorial(is)</span>
                </div>

                <button type="button" class="btn-toggle-group">
                    {{ grupoEstaAberto(grupo.nome) ? 'Recolher ▲' : 'Expandir ▼' }}
                </button>
            </div>

            <div v-show="grupoEstaAberto(grupo.nome)" class="help-grid">
                <div
                    v-for="item in grupo.itens"
                    :key="item.nmtitulo"
                    class="help-card"
                >
                    <div class="help-icon">▶</div>

                    <div class="help-content">
                        <h4>{{ item.nmtitulo }}</h4>
                        <p>{{ item.descricao }}</p>

                        <div class="help-meta">
                            <span>Vídeo</span>
                            <span>{{ formatTempoVideo(item.tempo_video) }}</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="btn-watch"
                        @click="abrirVideo(item)"
                    >
                        Assistir
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { Api } from '@/funcoes/apiExport';

const emit = defineEmits(['close']);

const lsDados = ref([]);
const msgAlerta = ref(null);
const isLoading = ref(false);
const gruposRecolhidos = ref({});


function closeForm() {
    emit('close');
}


const gruposAjuda = computed(() => {
    const map = {};

    lsDados.value.forEach(item => {
        const grupo = item.nmgrupo || 'OUTROS';

        if (!map[grupo]) {
            map[grupo] = {
                nome: grupo,
                itens: []
            };
        }

        map[grupo].itens.push(item);
    });

    return Object.values(map);
})


function grupoEstaAberto(nome) {
    return gruposRecolhidos.value[nome] !== true;
}

function alternarGrupo(nome) {
    gruposRecolhidos.value[nome] = !gruposRecolhidos.value[nome];
}

function abrirVideo(item) {
    if (!item.url) {
        msgAlerta.value = 'ATENÇÃO: Este tutorial ainda não possui link de acesso.';
        return;
    }

    window.open(item.url, '_blank');
}

function formatTempoVideo(segundos) {
    const total = Number(segundos || 0);

    if (total <= 0) return 'Tempo não informado';

    const min = Math.floor(total / 60);
    const seg = total % 60;

    if (seg === 0) return `${min} min`;

    return `${min}min ${String(seg).padStart(2, '0')}s`;
}

async function lerDados() {
    try {
        msgAlerta.value = null;
        isLoading.value = true;

        const req = await Api.genericPost('/listas/sys/help', { intparam1: 1 });
        lsDados.value = req ?? [];
        
        gruposRecolhidos.value = {};

        lsDados.value.forEach(item => {
            const grupo = item.nmgrupo || 'OUTROS';
            gruposRecolhidos.value[grupo] = true;
        });
    } catch (error) {
        msgAlerta.value = `ATENÇÃO: Não foi possível acessar dados do servidor: "${error.message}"`;
    } finally {
        isLoading.value = false;
    }
}

onMounted(lerDados);
</script>

<style scoped>
.help-page {
    width: 100%;
    text-align: left;
}

.help-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    background: #ffffff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 16px 18px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}

.help-header h2 {
    margin: 0;
    font-size: 22px;
    color: #1f2937;
}

.help-header p {
    margin: 4px 0 0;
    font-size: 13px;
    color: #64748b;
}

.help-group {
    background: #ffffff;
    border: 1px solid #d9e2ec;
    border-radius: 12px;
    padding: 14px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.help-group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 10px;
}

.help-group-header h3 {
    margin: 0;
    font-size: 16px;
    color: #1f2937;
}

.help-group-header span {
    font-size: 12px;
    color: #64748b;
}

.help-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 12px;
}

.help-card {
    display: flex;
    flex-direction: column;
    min-height: 180px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #f8fafc;
    padding: 14px;
    transition: 0.12s ease;
}

.help-card:hover {
    background: #ffffff;
    border-color: #94a3b8;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
}

.help-icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #1e3a8a;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    margin-bottom: 12px;
}

.help-content {
    flex: 1;
}

.help-content h4 {
    margin: 0;
    font-size: 15px;
    color: #1f2937;
    line-height: 1.25;
}

.help-content p {
    margin: 6px 0 0;
    font-size: 13px;
    color: #64748b;
    line-height: 1.35;
}

.help-meta {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-top: 12px;
    font-size: 11px;
    color: #475569;
}

.help-meta span {
    background: #e2e8f0;
    border-radius: 999px;
    padding: 4px 8px;
}

.btn-watch {
    width: 100%;
    margin-top: 14px;
    padding: 8px 12px;
    border: none;
    border-radius: 8px;
    background: #1e3a8a;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}

.btn-watch:hover {
    background: #1d4ed8;
}

.empty-state {
    border: 1px dashed #cbd5e1;
    background: #f8fafc;
    border-radius: 10px;
    padding: 22px;
    color: #64748b;
    text-align: center;
    font-size: 13px;
}

.help-group-header {
    cursor: pointer;
}

.btn-toggle-group {
    border: none;
    background: #e2e8f0;
    color: #334155;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
}

.btn-toggle-group:hover {
    background: #cbd5e1;
}

@media (max-width: 700px) {
    .help-header {
        flex-direction: column;
    }

    .help-grid {
        grid-template-columns: 1fr;
    }
}
</style>